import React, { useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import { EmailVerificationContainer, FieldsContainer, ResendCode } from "./EmailVerification.styles";
import { Button, InfoMessage, TitleSubtitle } from "../..";
import { VerificationCodeInput } from "../../VerificationCodeInput/VerificationCodeInput";
import { Button as MuiButton } from "@mui/material";
import { ButtonContainer, ButtonIcon, ButtonLabel, CTABackAndForthContainer } from "../../../pages/Register/Register.styles";
import { useRegisterContext } from "../../../contexts/RegisterContext";
import { confirmEmailAddress, resendConfirmationEmail } from "../../../services/AuthenticationService";
import { extractErrorMessageFromProblemDetails } from "src/utils/string-utils";

interface EmailVerificationProps extends WithRouterProps {
    onNextButtonClick: Function;
    onPrevButtonClick: Function;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
    onNextButtonClick,
    onPrevButtonClick,
}) => {
    const inputLength = 6;
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [verificationCodeHasError, setVerificationCodeHasError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [showError, setShowError] = useState<boolean>(false);
    const [showInfoMessage, setShowInfoMessage] = useState<boolean>(false);
    const [infoMessage, setInfoMessage] = useState<string>("");

    const { registerInfo } = useRegisterContext();

    const isContinueButtonDisabled = verificationCode.length < inputLength || verificationCodeHasError;

    const handleVerifyEmail = () => {
        setLoading(true);
        setShowError(false);
        confirmEmailAddress({
            token: verificationCode,
            user: { email: registerInfo.user.email }
        }).then((response) => {
            if (response.data) {
                onNextButtonClick();
            } else if (response.error) {
                setShowError(true);
                if (typeof response.error === "string") {
                    setErrorMessage(response.error);
                } else {
                    setErrorMessage(extractErrorMessageFromProblemDetails(response.error));
                }
            }
            setLoading(false);
        });
    };

    const handleResendCode = () => {
        setShowInfoMessage(false);
        setShowError(false);
        resendConfirmationEmail(registerInfo.user.email).then((res) => {
            if (res.status >= 400) {
                setShowError(true);
                setErrorMessage(res.errors?.message ? res.errors?.message : "An error occurred");
            } else {
                setShowInfoMessage(true);
                setInfoMessage("We have resent your verification code. If you don't receive it shortly, check your spam folder and try again.");
            }
        });
    };

    return (
        <EmailVerificationContainer>
            <TitleSubtitle
                data-cy="register-verify-email-title"
                size="sm"
                title="Verify Your Email"
                subTitle={`We just emailed you a code at ${registerInfo.user.email}, enter it below to verify your email address`} />
            <FieldsContainer>
                <VerificationCodeInput
                    errorFromOutside={true}
                    inputLength={6}
                    getConcatenatedValues={(value: string) => {
                        setVerificationCode(value);
                        setShowInfoMessage(false);
                    }}
                    hasError={(value: boolean) => { setVerificationCodeHasError(value) }} />
            </FieldsContainer>
            <ResendCode>
                Didn't get it? Check spam folder or
                <MuiButton
                    variant="text"
                    style={{ textTransform: 'none' }}
                    onClick={handleResendCode}>
                    Resend code
                </MuiButton>
            </ResendCode>
            {showError && (
                <InfoMessage
                    data-cy="register-email-verification-error"
                    variant="error"
                    hasBackground={true}
                    message={errorMessage}
                />
            )}
            {showInfoMessage && (
                <InfoMessage
                    data-cy="register-email-verification-info"
                    variant="info"
                    hasBackground={true}
                    message={infoMessage}
                />
            )}
            <CTABackAndForthContainer>
                <Button
                    size="md"
                    variant="tertiary"
                    onClick={() => onPrevButtonClick()}
                    data-cy="button-email-verification-back"
                    sx={{ padding: 0 }}
                >
                    <ButtonContainer>
                        <ButtonIcon icon="ArrowLeft" />
                        <ButtonLabel>Back</ButtonLabel>
                    </ButtonContainer>
                </Button>
                <Button
                    data-cy="register-email-verification-submit"
                    onClick={handleVerifyEmail}
                    disabled={isContinueButtonDisabled}
                    loading={loading}
                > Verify Email </Button>
            </CTABackAndForthContainer>
        </EmailVerificationContainer>
    );
};

EmailVerification.displayName = "EmailVerification";

const ComponentWithRouter = withRouter(EmailVerification)

export { ComponentWithRouter as EmailVerification };
